import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';

export interface OrganizadorTorneioLiga {
  id: string;
  nome: string;
  imagemUrl?: string | null;
  avatarUrl?: string | null;
}

export interface Organizador {
  id: string;
  nome: string;
  fotoUrl?: string | null;
  telefone?: string | null;
  nomeFuncao?: string | null;
  torneio?: OrganizadorTorneioLiga | null;
  liga?: OrganizadorTorneioLiga | null;
  usuario?: {
    id: string;
    nome: string;
    email?: string;
    imagemUrl?: string | null;
    username?: string | null;
    atleta?: { id: string } | null;
  } | null;
}

export interface OrganizadoresPorTorneioData {
  organizadoresPorTorneio: Organizador[];
}

export interface OrganizadoresPorTorneioVars {
  torneioId: string;
}

export const ORGANIZADORES_POR_TORNEIO = gql`
  query OrganizadoresPorTorneio($torneioId: ID!) {
    organizadoresPorTorneio(torneioId: $torneioId) {
      id
      nome
      fotoUrl
      telefone
      nomeFuncao
      torneio {
        id
        nome
        avatarUrl
      }
      usuario {
        id
        nome
        email
        imagemUrl
        username
      }
    }
  }
`;

export const ORGANIZADORES_POR_LIGA = gql`
  query OrganizadoresPorLiga($ligaId: ID!) {
    organizadoresPorLiga(ligaId: $ligaId) {
      id
      nome
      fotoUrl
      telefone
      nomeFuncao
      torneio {
        id
        nome
        avatarUrl
      }
      liga {
        id
        nome
        imagemUrl
      }
      usuario {
        id
        nome
        email
        imagemUrl
        username
        atleta {
          id
        }
      }
    }
  }
`;

export interface OrganizadorInput {
  torneioId?: string;
  ligaId?: string;
  nome: string;
  fotoUrl?: string | null;
  telefone?: string | null;
  usuarioId?: string | null;
  nomeFuncao?: string | null;
}

export const CRIAR_ORGANIZADOR = gql`
  mutation CriarOrganizador($input: OrganizadorInput!) {
    adicionarOrganizador(input: $input) {
      id
      nome
      fotoUrl
      telefone
      nomeFuncao
      usuario {
        id
        nome
        email
        imagemUrl
        username
      }
    }
  }
`;

export const CRIAR_ORGANIZADOR_LIGA = gql`
  mutation CriarOrganizadorLiga($input: OrganizadorInput!) {
    adicionarOrganizador(input: $input) {
      id
      nome
      fotoUrl
      telefone
      nomeFuncao
    }
  }
`;

export interface AtualizarOrganizadorVars {
  id: string;
  input: OrganizadorInput;
}

export const ATUALIZAR_ORGANIZADOR = gql`
  mutation AtualizarOrganizador($id: ID!, $input: OrganizadorInput!) {
    atualizarOrganizador(id: $id, input: $input) {
      id
      nome
      fotoUrl
      telefone
      nomeFuncao
    }
  }
`;

export const REMOVER_ORGANIZADOR = gql`
  mutation RemoverOrganizador($id: ID!) {
    removerOrganizador(id: $id)
  }
`;

export const UPLOAD_FOTO_ORGANIZADOR = gql`
  mutation UploadFotoOrganizador($id: ID!, $file: Upload!) {
    uploadFotoOrganizador(id: $id, file: $file) {
      fotoUrl
    }
  }
`;

export const organizadoresPorTorneio = async (
  torneioId: string,
): Promise<Organizador[]> => {
  const { data } = await apolloClient.query<
    OrganizadoresPorTorneioData,
    OrganizadoresPorTorneioVars
  >({
    query: ORGANIZADORES_POR_TORNEIO,
    variables: { torneioId },
    fetchPolicy: 'no-cache',
  });
  return data.organizadoresPorTorneio;
};

export interface OrganizadoresPorLigaData {
  organizadoresPorLiga: Organizador[];
}

export interface OrganizadoresPorLigaVars {
  ligaId: string;
}

export const organizadoresPorLiga = async (
  ligaId: string,
): Promise<Organizador[]> => {
  const { data } = await apolloClient.query<
    OrganizadoresPorLigaData,
    OrganizadoresPorLigaVars
  >({
    query: ORGANIZADORES_POR_LIGA,
    variables: { ligaId },
    fetchPolicy: 'no-cache',
  });
  return data.organizadoresPorLiga;
};

export const criarOrganizador = async (
  input: OrganizadorInput,
): Promise<Organizador | null> => {
  const { data } = await apolloClient.mutate<
    { adicionarOrganizador: Organizador },
    { input: OrganizadorInput }
  >({
    mutation: CRIAR_ORGANIZADOR,
    variables: { input },
  });
  return data?.adicionarOrganizador ?? null;
};

export const criarOrganizadorLiga = async (
  input: OrganizadorInput,
): Promise<Organizador | null> => {
  const { data } = await apolloClient.mutate<
    { adicionarOrganizador: Organizador },
    { input: OrganizadorInput }
  >({
    mutation: CRIAR_ORGANIZADOR_LIGA,
    variables: { input },
  });
  return data?.adicionarOrganizador ?? null;
};

export const atualizarOrganizador = async (
  vars: AtualizarOrganizadorVars,
): Promise<Organizador | null> => {
  const { data } = await apolloClient.mutate<
    { atualizarOrganizador: Organizador },
    AtualizarOrganizadorVars
  >({
    mutation: ATUALIZAR_ORGANIZADOR,
    variables: vars,
  });
  return data?.atualizarOrganizador ?? null;
};

export const removerOrganizador = async (
  id: string,
): Promise<boolean> => {
  const { data } = await apolloClient.mutate<
    { removerOrganizador: boolean },
    { id: string }
  >({
    mutation: REMOVER_ORGANIZADOR,
    variables: { id },
  });
  return data?.removerOrganizador ?? false;
};

const restUrl = process.env.REACT_APP_GRAPHQL_ENDPOINT + 'api/';

export const uploadFotoOrganizador = async (
  id: string,
  arquivo: File,
): Promise<{ fotoUrl: string }> => {
  const formData = new FormData();
  formData.append('file', arquivo);

  const token = localStorage.getItem('token');
  const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
  if (token) {
    headers['authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post<string>(
      `${restUrl}torneios/organizadores/${id}/foto`,
      formData,
      { headers },
    );
    return { fotoUrl: response.data ?? '' };
  } catch (error: any) {
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    let message = 'Erro inesperado. Tente novamente.';
    if (error.response?.data?.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    dispatchGlobalError(message);
    throw error;
  }
};

